/**
 * The return page's poll, wired to React.
 *
 * Everything that DECIDES anything lives in ./poll.ts and ./return-url.ts and
 * is tested there. This module is only the plumbing: an interval against an
 * external system (the network, the browser's timers, and finally
 * `location.replace`), which is the sanctioned use of an effect under
 * infra/audit/policies/react-useeffect-discipline/POLICY.md — "set up a timer"
 * and "fetch data", both with cleanup.
 *
 * Two structural departures from the monorepo's return-client.tsx, neither of
 * which touches the polling contract:
 *
 *   1. ONE effect, not two. The source settled to `success`, stored the
 *      redirect URL in state, and then a SECOND effect watched for that pair
 *      to appear and armed the redirect — an effect chain (POLICY §2.1), and
 *      one that spent a render pass getting from "we decided" to "we acted".
 *      Here the poll arms its own redirect at the moment it decides, in the
 *      same place, and the single cleanup below disarms it. React commits the
 *      confirmed state long before the 1.2s hand-off, so the shopper still
 *      sees it.
 *
 *   2. No `redirectedRef`. The source guarded the redirect with a ref that
 *      was never reset, which under `StrictMode` — which this app's main.tsx
 *      uses — would latch on the first mount's effect and leave the remounted
 *      one unable to ever redirect. Cleanup alone is the correct guard: it
 *      clears the pending timer, and re-running the effect simply re-polls.
 */

import { useEffect, useState } from "react";
import {
  INITIAL_INTERVAL_MS,
  REDIRECT_DELAY_MS,
  backOff,
  decide,
  isExhausted,
} from "./poll";
import { buildReceiptReturnUrl } from "./return-url";
import {
  SESSION_REQUEST_INIT,
  parseSessionStatus,
  sessionEndpoint,
  type SessionState,
  type SessionStatus,
} from "./session-status";

/**
 * What the page renders. `polling` is the only non-terminal one; `timeout`
 * means the budget ran out with the session still unconfirmed, which is a
 * "check back" state and NOT a failed payment.
 */
export type ReturnPhase =
  | "polling"
  | "success"
  | "expired"
  | "abandoned"
  | "timeout";

export interface SessionPoll {
  readonly phase: ReturnPhase;
  /**
   * The last status the API actually reported, kept separately from `phase`
   * so the page can show the two facts it knows apart: a session can be
   * `paid` while the phase is still `polling`, because the receipt has not
   * been issued yet. That distinction is the single most reassuring thing
   * this page can tell someone who just entered their card details, and
   * collapsing it into `phase` would throw it away.
   */
  readonly lastState: SessionState | null;
  /** The consumer app's return URL, once the API has disclosed one. */
  readonly returnUrl: string | undefined;
  /** Where the browser is about to go — null when there is nowhere to go. */
  readonly redirectUrl: string | null;
}

export function useSessionPoll(
  sessionId: string | null,
  orderId: string | null,
): SessionPoll {
  // A missing session id can never resolve, so it is the INITIAL state rather
  // than something an effect sets on mount — no synchronous setState in an
  // effect, and no frame where the page claims to be confirming a payment it
  // was never told about.
  //
  // Falsy rather than `=== null`, matching the source's `sessionId ?` and
  // `if (!sessionId)`: a bare `?session_id=` in the URL reads back as the
  // empty string, not null. Treating that as present would poll
  // `/api/session/` — a path with no id on it — eighteen times over two
  // minutes before giving up, instead of landing here immediately.
  const [poll, setPoll] = useState<SessionPoll>(() => ({
    phase: sessionId ? "polling" : "timeout",
    lastState: null,
    returnUrl: undefined,
    redirectUrl: null,
  }));

  useEffect(() => {
    if (!sessionId) return;

    const startedAt = Date.now();
    let interval: number = INITIAL_INTERVAL_MS;
    let cancelled = false;
    // One handle for whichever timer is outstanding — the next poll tick, or
    // the hand-off to the consumer app. They are mutually exclusive: arming
    // the redirect is the last thing this effect ever does.
    let timer: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = () => {
      if (cancelled) return;
      // Budget checked before scheduling, not before issuing: the final
      // request may fire at any moment right up to the boundary.
      if (isExhausted(Date.now() - startedAt)) {
        setPoll((prev) => ({ ...prev, phase: "timeout" }));
        return;
      }
      timer = setTimeout(() => void runPoll(), interval);
      interval = backOff(interval);
    };

    const runPoll = async (): Promise<void> => {
      if (cancelled) return;

      let seen: SessionStatus | null = null;
      try {
        const res = await fetch(
          sessionEndpoint(sessionId),
          SESSION_REQUEST_INIT,
        );
        if (cancelled) return;
        // A non-OK response is transient by assumption and retried until the
        // budget runs out. A 404 here is far likelier to be an edge node that
        // has not caught up than a session that does not exist, and giving up
        // on it would strand a shopper whose payment is fine.
        if (res.ok) seen = parseSessionStatus(await res.json());
      } catch {
        // Network hiccup, or a body that was not JSON. Retried on the next
        // scheduled tick, same as any inconclusive answer.
      }
      if (cancelled) return;

      const decision = decide(seen);

      if (decision.kind === "paid") {
        const disclosed = seen?.return_url;
        const redirectUrl =
          disclosed !== undefined
            ? buildReceiptReturnUrl(
                disclosed,
                decision.receipt,
                // The response's own order id wins; the query-string one is
                // the fallback for a session that does not echo it back.
                seen?.order_id ?? orderId ?? undefined,
              )
            : null;

        // `?? prev.returnUrl` rather than a plain assignment: the source only
        // ever wrote this state when a response actually carried a return URL,
        // making it sticky across polls. The endpoint re-authorises the stored
        // URL on EVERY read and withholds it when the host has fallen off the
        // allow-list, so a later response legitimately dropping the field must
        // not erase what an earlier one disclosed.
        setPoll((prev) => ({
          phase: "success",
          lastState: "paid",
          returnUrl: disclosed ?? prev.returnUrl,
          redirectUrl,
        }));

        if (redirectUrl !== null) {
          // `replace`, not `assign`: the shopper must not be able to land
          // back on this page with the back button and re-poll a session that
          // is already spent.
          timer = setTimeout(() => {
            window.location.replace(redirectUrl);
          }, REDIRECT_DELAY_MS);
        }
        return;
      }

      if (decision.kind === "expired" || decision.kind === "abandoned") {
        setPoll((prev) => ({
          ...prev,
          phase: decision.kind,
          lastState: decision.kind,
          returnUrl: seen?.return_url ?? prev.returnUrl,
        }));
        return;
      }

      // Still waiting — but publish what we learned. `created` and
      // `paid`-without-a-receipt are different waits and the page says so.
      if (seen !== null) {
        const state = seen.status;
        const disclosed = seen.return_url;
        setPoll((prev) => ({
          ...prev,
          lastState: state,
          // Never un-disclose a return URL we have already been given.
          returnUrl: disclosed ?? prev.returnUrl,
        }));
      }
      scheduleNext();
    };

    void runPoll();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [sessionId, orderId]);

  return poll;
}
